// ContactArchiv — einheitliche, ausklappbare, filterbare Verlaufs-Timeline der
// Kontakt-Detailansicht. Sie ist die Sektion „Verlauf / Archiv" im
// Kundentagebuch-Reiter (früher ein eigener Reiter „Archiv"). Führt ALLE Quellen
// in EIN Modell zusammen: Vorgänge, Todos, Zusammenführungen,
// Verantwortlichkeits-Änderungen, Feld-/Status-Archiv (ContactFieldArchive —
// enthält die Bereinigungs-Status-Änderungen) und den technischen
// Änderungsverlauf (AuditLog) — seit 08.09.2026 die Kommunikation des
// Kunden (Mails, Anrufe, Dokumente) und seit 14.09.2026 als zehnte Art die
// Notizen aus Vorgängen und To-dos (`mitNotizen`). Ausklapp-Muster wie
// VorgangTodoRow.
//
// Rein lesend; nutzt bestehende Endpoints (vorgang/todo, merge-history,
// ownership-history, field-archive, timeline, mail/links, telephony/calls,
// documents, kontakt/:id/alle-notizen). Keine Backend-Änderung.

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import {
  Briefcase20Regular,
  Note20Regular,
  PeopleSwap20Regular,
  Archive20Regular,
  History20Regular,
  Mail20Regular,
  Call20Regular,
  Comment20Regular,
  Document20Regular,
} from "@fluentui/react-icons";
import { vorgangService, type VorgangDto, type TodoDto } from "../services/vorgangService";
import { mailLinkService } from "../services/mailLinkService";
import { telephonyService } from "../services/telephonyService";
import { documentService } from "../services/documentService";
import {
  anrufEintraege,
  dokumentEintraege,
  mailEintraege,
  type KommEintrag,
} from "./kontaktArchiv/kommunikationEintraege";
import { notizEintraege, type NotizEintrag } from "./kontaktArchiv/notizEintraege";
import { vorgangSprungLink } from "./vorgang/v4/sprungLink";
import { ladeKundenNotizen } from "./kundentagebuch/kundenNotizen";
import { viaCompanyLabel } from "./contactVorgaengeHelpers";
import { apiFetch } from "../utils/apiFetch";
import { apiUrl } from "../utils/api";
import { useReferenceData } from "../context/ReferenceDataContext";
import { FIELD_LABELS } from "./MergeFieldArchive";
import { REFINED } from "./risikoanalyseStep/tokens";

type ArchiveKind =
  | "vorgang"
  | "todo"
  | "zusammenfuehrung"
  | "verantwortlichkeit"
  | "feld"
  | "audit"
  // PO 08.09.2026: der Verlauf zeigt auch die Kommunikation — dieselben drei
  // Arten wie im Vorgangs-Verlauf (V3), nur ohne dessen Aktionen.
  | "mail"
  | "anruf"
  | "dokument"
  // PO 14.09.2026: „kundentagebuch da nur neue notizen, notizen zu vorgang und
  // todos im archiv anzeigen" — die Notizfläche oben zeigt seitdem nur noch die
  // eigenen Notizen, die aus Vorgang und To-do stehen hier (nur lesbar).
  | "notiz";

/** Eine Detailzeile im aufgeklappten Bereich: entweder alt→neu ODER Freitext. */
type DetailRow = { label?: string; old?: string | null; neu?: string | null; text?: string };

type ArchiveEntry = {
  id: string;
  ts: Date;
  kind: ArchiveKind;
  title: string;
  summary?: string;
  details?: DetailRow[];
  actor?: string;
  /**
   * Route, in die der Titel springt („/vorgaenge/42"); fehlt sie, gibt es
   * nichts zu öffnen und der Titel bleibt Text. Der Rest der Zeile klappt
   * weiterhin auf — beides am selben Klick wäre nicht zu treffen.
   */
  ziel?: string;
};

const KIND_LABELS: Record<ArchiveKind, string> = {
  vorgang: "Vorgang",
  todo: "Todo",
  zusammenfuehrung: "Zusammenführung",
  verantwortlichkeit: "Verantwortlichkeit",
  feld: "Feldänderung",
  audit: "Änderung",
  mail: "Mail",
  anruf: "Anruf",
  dokument: "Dokument",
  notiz: "Notiz",
};

const KIND_COLORS: Record<ArchiveKind, { fg: string; bg: string }> = {
  vorgang: { fg: "#6d4c9e", bg: "#f1ecf8" },
  todo: { fg: "#2e7d32", bg: "#e9f4ee" },
  zusammenfuehrung: { fg: "#00838f", bg: "#e0f2f4" },
  verantwortlichkeit: { fg: "#8d6e63", bg: "#efe9e7" },
  feld: { fg: REFINED.aub, bg: REFINED.aub50 },
  audit: { fg: "#2563eb", bg: "#e8eefc" },
  mail: { fg: "#1565c0", bg: "#e3f0fb" },
  anruf: { fg: "#00695c", bg: "#e0f0ee" },
  dokument: { fg: "#5d4037", bg: "#f0eae7" },
  // Dasselbe Gelb, in dem im Kundentagebuch der Entwurfstext steht.
  notiz: { fg: "#8a5a00", bg: "#fdf3e0" },
};

const KIND_ICONS: Record<ArchiveKind, React.ReactNode> = {
  vorgang: <Briefcase20Regular />,
  todo: <Note20Regular />,
  zusammenfuehrung: <PeopleSwap20Regular />,
  verantwortlichkeit: <PeopleSwap20Regular />,
  feld: <Archive20Regular />,
  audit: <History20Regular />,
  mail: <Mail20Regular />,
  anruf: <Call20Regular />,
  dokument: <Document20Regular />,
  notiz: <Comment20Regular />,
};

const safeDate = (raw?: string | null): Date | null => {
  if (!raw) return null;
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
};

const fmtDateTime = (d: Date) =>
  d.toLocaleString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

const pad = (n: number) => String(n).padStart(2, "0");
/** Date → Wert für <input type="datetime-local"> (lokale Zeit, ohne Sekunden). */
const toLocalInput = (d: Date) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;

/** Audit-/Roh-Werte menschenlesbar machen. */
const showRaw = (v: unknown): string => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "Ja" : "Nein";
  if (typeof v === "object") {
    try {
      return JSON.stringify(v);
    } catch {
      return String(v);
    }
  }
  return String(v);
};

// ── DTOs der Quellen ────────────────────────────────────────────────────────
type MergeHistoryDto = {
  id: number;
  loserContactId: number;
  loserName?: string | null;
  loserContactNbr?: string | null;
  mergedByName?: string | null;
  mergedAt?: string | null;
};
type OwnershipChange = { old: string | null; new: string | null };
type OwnershipHistoryDto = {
  id: number;
  at: string;
  by: string | null;
  fo?: OwnershipChange;
  boType?: OwnershipChange;
  boTeamId?: OwnershipChange;
  boUserId?: OwnershipChange;
};
type FieldArchiveDto = {
  id: number;
  column: string;
  oldValue: string | null;
  newValue: string | null;
  sourceContactId: number | null;
  sourceName: string | null;
  mergedByName?: string | null;
  mergedAt?: string | null;
};
type AuditAction = "INSERT" | "UPDATE" | "DELETE";
type AuditLogDto = {
  id: number;
  action: AuditAction;
  changes?: Record<string, { old: unknown; new: unknown }> | null;
  createdAt: string;
  userId?: string | number | null;
};
type StatusOption = { id: number; status: string };

const AUDIT_LABEL: Record<AuditAction, string> = {
  INSERT: "Erstellt",
  UPDATE: "Geändert",
  DELETE: "Gelöscht",
};

// ── Schnellbereiche ───────────────────────────────────────────────────────────
type RangeKey = "alle" | "heute" | "7" | "30" | "jahr";
const RANGES: { key: RangeKey; label: string }[] = [
  { key: "alle", label: "Alle" },
  { key: "heute", label: "Heute" },
  { key: "7", label: "7 Tage" },
  { key: "30", label: "30 Tage" },
  { key: "jahr", label: "Jahr" },
];

const rangeBounds = (key: RangeKey): { from: Date | null; to: Date | null } => {
  if (key === "alle") return { from: null, to: null };
  const now = new Date();
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
  if (key === "heute") {
    return { from: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0), to: end };
  }
  if (key === "jahr") {
    return { from: new Date(now.getFullYear(), 0, 1, 0, 0, 0), to: end };
  }
  const days = key === "7" ? 7 : 30;
  const from = new Date(now);
  from.setDate(from.getDate() - days);
  return { from, to: now };
};

type Props = {
  contactId: number;
  /** Ohne äußeres Padding rendern — den Rahmen liefert die Klapp-Sektion. */
  embedded?: boolean;
  /** Meldet die Zahl der Verlaufseinträge nach oben (Zähler im Sektionskopf). */
  onCountChange?: (count: number) => void;
  /**
   * Die Notizen aus Vorgängen und To-dos als zehnte Art mitführen
   * (PO 14.09.2026). Vorgabe `false` = das bisherige Verhalten: wer diesen
   * Verlauf woanders einbindet, bekommt keine Notizen und keinen zusätzlichen
   * Abruf. Eingeschaltet wird sie dort, wo die Notizfläche darüber sie
   * weggelassen hat — im Kundentagebuch-Reiter.
   */
  mitNotizen?: boolean;
};

const ContactArchiv = ({ contactId, embedded = false, onCountChange, mitNotizen = false }: Props) => {
  const { users, appTeams } = useReferenceData();
  const navigate = useNavigate();
  const [vorgaenge, setVorgaenge] = useState<VorgangDto[]>([]);
  const [todos, setTodos] = useState<TodoDto[]>([]);
  const [merges, setMerges] = useState<MergeHistoryDto[]>([]);
  const [ownership, setOwnership] = useState<OwnershipHistoryDto[]>([]);
  const [fields, setFields] = useState<FieldArchiveDto[]>([]);
  const [audit, setAudit] = useState<AuditLogDto[]>([]);
  const [statuses, setStatuses] = useState<StatusOption[]>([]);
  const [komm, setKomm] = useState<KommEintrag[]>([]);
  const [notizen, setNotizen] = useState<NotizEintrag[]>([]);
  const [loading, setLoading] = useState(true);

  // Leere Menge = alle Arten. Ohne diesen Filter wäre der Verlauf seit der
  // Kommunikation (Mails/Anrufe/Dokumente) nur noch am Stück lesbar.
  const [arten, setArten] = useState<Set<ArchiveKind>>(new Set());
  const [range, setRange] = useState<RangeKey>("alle");
  const [from, setFrom] = useState<Date | null>(null);
  const [to, setTo] = useState<Date | null>(null);
  const [open, setOpen] = useState<Set<string>>(new Set());

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    // Beim Kontaktwechsel den Art-Filter zurücksetzen: der neue Kontakt hat
    // vielleicht gar keine Anrufe — die Auswahl bliebe sonst als leere Liste
    // stehen, deren Zurücksetzen-Knopf mit der Art verschwunden ist.
    setArten(new Set());
    const hasId = Number.isFinite(contactId) && contactId > 0;
    const get = <T,>(path: string): Promise<T[]> =>
      hasId ? apiFetch<T[]>(path).then((r) => r.data ?? []).catch(() => [] as T[]) : Promise.resolve([] as T[]);

    const auditP: Promise<AuditLogDto[]> = hasId
      ? fetch(apiUrl("timeline", { entityName: "Contact", entityId: String(contactId), limit: 200 }), {
          headers: { Accept: "application/json" },
        })
          .then((r) => (r.ok ? r.json() : []))
          .then((raw) => (Array.isArray(raw) ? (raw as AuditLogDto[]) : []))
          .catch(() => [] as AuditLogDto[])
      : Promise.resolve([] as AuditLogDto[]);

    // Vorgänge/Todos strikt PRO KONTAKT laden. Früher zog diese Ansicht
    // getAllVorgaenge()/getAllTodos() — also mandantenweit ALLES (ohne Limit,
    // zusätzlich in localStorage gecacht) — und filterte im Browser. Die beiden
    // Endpoints hier prüfen die per-Kontakt-RBAC selbst und liefern zusätzlich
    // die Vorgänge verbundener Firmen mit.
    const vorgaengeP = hasId
      ? vorgangService.getVorgaengeByContact(contactId).catch(() => [] as VorgangDto[])
      : Promise.resolve([] as VorgangDto[]);
    const todosP = hasId
      ? vorgangService.getTodosByLinked("KUNDE", contactId).catch(() => [] as TodoDto[])
      : Promise.resolve([] as TodoDto[]);

    // Kommunikation (PO 08.09.2026). JEDE Quelle fängt ihren Fehler selbst ab:
    // ein fehlender Postfach-Zugriff oder eine stille Telefonanlage darf den
    // ganzen Verlauf nicht kippen, sondern nur die eigene Zeile auslassen.
    const kommP = (
      lade: () => Promise<KommEintrag[]>,
      was: string,
    ): Promise<KommEintrag[]> =>
      hasId
        ? lade().catch((err) => {
            console.error(`[ContactArchiv] ${was} laden fehlgeschlagen`, err);
            return [] as KommEintrag[];
          })
        : Promise.resolve([] as KommEintrag[]);

    const mailsP = kommP(() => mailLinkService.listByContact(contactId).then(mailEintraege), "Mails");
    const anrufeP = kommP(
      () => telephonyService.listCalls({ contactId, limit: 200 }).then((r) => anrufEintraege(r.calls ?? [], contactId)),
      "Anrufe",
    );
    // Grenze der Quelle: `GET /documents?contactId=` deckt Kontakt-, Vertrags-,
    // Schaden- und Risiko-Dokumente ab, NICHT die, die allein an einem Vorgang
    // oder To-do des Kunden hängen (document.service.ts:319-327). Der Verlauf
    // ist an dieser Stelle also unvollständig — das ist kein Fehler.
    const dokumenteP = kommP(
      () => documentService.list({ contactId, limit: 200 }).then(dokumentEintraege),
      "Dokumente",
    );

    // Die Notizen aus Vorgängen und To-dos (PO 14.09.2026) — mit eigenem
    // Fehler-Abfangen wie jede andere Quelle. Derselbe Endpunkt speist die
    // Notizfläche oben; das ist ein zweiter Abruf, aber KEIN doppelter beim
    // Öffnen des Reiters: die Klapp-Sektion hängt diesen Baum aus, solange das
    // Archiv zu ist, und lädt erst beim Aufklappen.
    const notizenP: Promise<NotizEintrag[]> =
      hasId && mitNotizen
        ? ladeKundenNotizen(contactId)
            .then(notizEintraege)
            .catch((err) => {
              console.error("[ContactArchiv] Notizen laden fehlgeschlagen", err);
              return [] as NotizEintrag[];
            })
        : Promise.resolve([] as NotizEintrag[]);

    Promise.all([
      vorgaengeP,
      todosP,
      get<MergeHistoryDto>(`duplicate-candidates/${contactId}/merge-history`),
      get<OwnershipHistoryDto>(`contact/${contactId}/ownership-history`),
      get<FieldArchiveDto>(`duplicate-candidates/${contactId}/field-archive`),
      auditP,
      apiFetch<StatusOption[]>("contactstatus").then((r) => r.data ?? []).catch(() => [] as StatusOption[]),
      mailsP,
      anrufeP,
      dokumenteP,
      notizenP,
    ]).then(([vs, ts, ms, os, fs, au, st, mails, anrufe, dokumente, notizenEintraege]) => {
      if (cancelled) return;
      setVorgaenge(vs);
      setTodos(ts);
      setMerges(ms);
      setOwnership(os);
      setFields(fs);
      setAudit(au);
      setStatuses(st);
      setKomm([...mails, ...anrufe, ...dokumente]);
      setNotizen(notizenEintraege);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [contactId, mitNotizen]);

  const entries = useMemo<ArchiveEntry[]>(() => {
    const out: ArchiveEntry[] = [];
    const userMap = new Map(users.map((u) => [u.id, u.name] as const));
    const teamMap = new Map(appTeams.map((t) => [t.id, t.name] as const));
    const statusMap = new Map(statuses.map((s) => [String(s.id), s.status] as const));
    const userName = (id: string | null | undefined) =>
      id == null || id === "" ? "—" : userMap.get(String(id)) ?? "Unbekannt";
    const teamName = (id: string | null) => (id ? teamMap.get(id) ?? "Unbekanntes Team" : "—");
    const boTypeLabel = (v: string | null) =>
      v === "TEAM" ? "Team" : v === "USER" ? "Person" : v === "NONE" ? "Keiner" : v ?? "—";

    // Kein clientseitiger Filter mehr: die Quelle ist bereits kontakt-scoped.
    // Ein Filter auf `v.kunde?.id === contactId` würde hier sogar schaden — er
    // verwürfe die Vorgänge verbundener Firmen, die der Vorgänge-Bereich
    // darüber sehr wohl zeigt. Diese Vorgänge gehören aber NICHT diesem
    // Kontakt, deshalb tragen sie — wie in der Vorgangsliste — die Herkunft im
    // Titel; ohne den Zusatz sähe es aus, als sei es ein eigener Vorgang.
    for (const v of vorgaenge) {
      const via = viaCompanyLabel(v);
      out.push({
        id: `vorgang-${v.id}`,
        ts: safeDate(v.createdAt) ?? safeDate(v.frist) ?? new Date(),
        kind: "vorgang",
        title: via ? `${v.titel} · ${via}` : v.titel,
        summary: v.beschreibung || undefined,
        details: v.beschreibung ? [{ text: v.beschreibung }] : undefined,
      });
    }

    for (const t of todos) {
      out.push({
        id: `todo-${t.id}`,
        ts: safeDate(t.createdAt) ?? safeDate(t.frist) ?? safeDate(t.twv) ?? new Date(),
        kind: "todo",
        title: t.titel,
        summary: t.beschreibung || `Status: ${t.status}`,
        details: [
          ...(t.beschreibung ? [{ text: t.beschreibung }] : []),
          { label: "Status", text: t.status },
          ...(t.frist ? [{ label: "Frist", text: t.frist }] : []),
        ],
        // Klick öffnet den Vorgang MIT aufgeklapptem To-do (User 01.11.2026).
        ...((t.vorgang?.id ?? t.vorgangId) != null
          ? { ziel: vorgangSprungLink((t.vorgang?.id ?? t.vorgangId) as number, { todoId: t.id }) }
          : {}),
      });
    }

    for (const m of merges) {
      const name = m.loserName?.trim() || `Kontakt #${m.loserContactId}`;
      const nbr = m.loserContactNbr ? ` (Nr. ${m.loserContactNbr})` : "";
      out.push({
        id: `merge-${m.id}`,
        ts: safeDate(m.mergedAt) ?? new Date(),
        kind: "zusammenfuehrung",
        title: `Kontakt „${name}"${nbr} zusammengeführt`,
        summary: "Duplikat in diesen Kontakt zusammengeführt",
        details: [
          {
            text: `Der Kontakt „${name}" wurde als Duplikat in diesen Kontakt zusammengeführt. Seine Daten wurden übernommen und archiviert.`,
          },
        ],
        actor: m.mergedByName || undefined,
      });
    }

    for (const o of ownership) {
      const rows: DetailRow[] = [];
      if (o.fo) rows.push({ label: "Frontoffice", old: userName(o.fo.old), neu: userName(o.fo.new) });
      if (o.boTeamId) rows.push({ label: "BO-Team", old: teamName(o.boTeamId.old), neu: teamName(o.boTeamId.new) });
      if (o.boUserId) rows.push({ label: "BO-Person", old: userName(o.boUserId.old), neu: userName(o.boUserId.new) });
      if (!o.boTeamId && !o.boUserId && o.boType)
        rows.push({ label: "BO-Typ", old: boTypeLabel(o.boType.old), neu: boTypeLabel(o.boType.new) });
      out.push({
        id: `ownership-${o.id}`,
        ts: safeDate(o.at) ?? new Date(),
        kind: "verantwortlichkeit",
        title: "Verantwortlichkeit geändert",
        summary: rows.map((r) => `${r.label}: ${r.old} → ${r.neu}`).join("; ") || undefined,
        details: rows,
        actor: o.by ? userName(o.by) : undefined,
      });
    }

    for (const f of fields) {
      const label = FIELD_LABELS[f.column] ?? f.column;
      out.push({
        id: `field-${f.id}`,
        ts: safeDate(f.mergedAt) ?? new Date(),
        kind: "feld",
        title: `${label}: ${f.oldValue ?? "—"} → ${f.newValue ?? "—"}`,
        details: [{ label, old: f.oldValue, neu: f.newValue }],
        actor:
          f.sourceContactId != null
            ? `Übernommen aus „${f.sourceName ?? "zusammengeführter Kontakt"}"`
            : f.sourceName ?? undefined,
      });
    }

    // Bekannte FK-Felder im Audit-Log lesbar auflösen (Status/User/Team).
    const resolveAudit = (field: string, v: unknown): string => {
      if (v == null || v === "") return "—";
      const key = String(v);
      if (field === "contactStatusId") return statusMap.get(key) ?? showRaw(v);
      if (field === "foUserId" || field === "boUserId" || field === "createdByUserId" || field === "ownerUserId")
        return userMap.get(key) ?? showRaw(v);
      if (field === "boTeamId" || field === "ownerTeamId") return teamMap.get(key) ?? showRaw(v);
      return showRaw(v);
    };
    for (const a of audit) {
      const changeRows: DetailRow[] = Object.entries(a.changes ?? {}).map(([field, ch]) => ({
        label: FIELD_LABELS[field] ?? field,
        old: resolveAudit(field, ch?.old),
        neu: resolveAudit(field, ch?.new),
      }));
      const fieldList = changeRows.map((r) => r.label).filter(Boolean).join(", ");
      out.push({
        id: `audit-${a.id}`,
        ts: safeDate(a.createdAt) ?? new Date(),
        kind: "audit",
        title: AUDIT_LABEL[a.action] ?? "Änderung",
        summary: fieldList || undefined,
        details: changeRows.length ? changeRows : undefined,
        actor: a.userId != null ? userName(a.userId as string) : undefined,
      });
    }

    // Mails, Anrufe und Dokumente sind schon im Zielmodell (id, ts, kind,
    // title, summary, actor); nur der Freitext wandert in den Aufklappbereich.
    for (const k of komm) {
      out.push({
        id: k.id,
        ts: k.ts,
        kind: k.kind,
        title: k.title,
        summary: k.summary,
        details: k.text ? [{ text: k.text }] : undefined,
        actor: k.actor,
      });
    }

    // Die Notizen aus Vorgängen und To-dos (PO 14.09.2026). Sie sind hier
    // NUR LESBAR: eine Vorgangs-Notiz gehört ihrem Vorgang, ein zweiter
    // Bearbeitungsort für denselben Text wäre eine Einladung zum
    // Auseinanderlaufen. Deshalb ein Sprungziel statt einer Aktion — geändert
    // wird dort, wo die Notiz zu Hause ist.
    for (const n of notizen) {
      out.push({
        id: n.id,
        ts: n.ts,
        kind: "notiz",
        title: n.titel,
        summary: n.herkunft,
        details: [{ text: n.text }, { label: "Herkunft", text: n.herkunft }],
        actor: n.autor ? userName(n.autor) : undefined,
        ...(n.ziel ? { ziel: n.ziel } : {}),
      });
    }

    return out.sort((a, b) => b.ts.getTime() - a.ts.getTime());
  }, [vorgaenge, todos, merges, ownership, fields, audit, statuses, komm, notizen, users, appTeams]);

  const filtered = useMemo(
    () =>
      entries.filter(
        (e) => (!from || e.ts >= from) && (!to || e.ts <= to) && (arten.size === 0 || arten.has(e.kind)),
      ),
    [entries, from, to, arten],
  );

  // Nur Arten anbieten, die dieser Kontakt auch hat — ein Knopf, der die Liste
  // garantiert leert, ist keine Hilfe.
  const vorhandeneArten = useMemo(() => {
    const da = new Set(entries.map((e) => e.kind));
    return (Object.keys(KIND_LABELS) as ArchiveKind[]).filter((k) => da.has(k));
  }, [entries]);

  const toggleArt = (k: ArchiveKind) =>
    setArten((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });

  // Zähler nach außen melden — die ungefilterte Gesamtzahl, damit der
  // Sektionskopf nicht mit dem Zeitraum-Filter springt.
  useEffect(() => {
    if (!loading) onCountChange?.(entries.length);
  }, [loading, entries.length, onCountChange]);

  const applyRange = (key: RangeKey) => {
    setRange(key);
    const b = rangeBounds(key);
    setFrom(b.from);
    setTo(b.to);
  };
  const onInput = (which: "from" | "to", value: string) => {
    setRange("custom" as RangeKey);
    const d = value ? new Date(value) : null;
    if (which === "from") setFrom(d);
    else setTo(d);
  };
  const toggle = (id: string) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <div style={{ padding: embedded ? 0 : 16, display: "flex", flexDirection: "column", gap: 12 }}>
      {/* ── Filter-Leiste ── */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "flex-end",
          gap: 12,
          padding: "12px 14px",
          border: `1px solid ${REFINED.line}`,
          borderRadius: 10,
          backgroundColor: "#fff",
        }}
      >
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {RANGES.map((r) => {
            const active = range === r.key;
            return (
              <button
                key={r.key}
                type="button"
                onClick={() => applyRange(r.key)}
                style={{
                  border: `1.5px solid ${active ? REFINED.aub : REFINED.line}`,
                  backgroundColor: active ? REFINED.aub : "#fff",
                  color: active ? "#fff" : REFINED.ink2,
                  borderRadius: 999,
                  padding: "5px 12px",
                  fontSize: 12.5,
                  fontWeight: 600,
                  cursor: "pointer",
                  fontFamily: "inherit",
                }}
              >
                {r.label}
              </button>
            );
          })}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: REFINED.ink2, letterSpacing: "0.4px" }}>AB</span>
          <input
            type="datetime-local"
            value={from ? toLocalInput(from) : ""}
            onChange={(e) => onInput("from", e.target.value)}
            style={{
              height: 34,
              border: `1.5px solid ${REFINED.line}`,
              borderRadius: 8,
              padding: "0 8px",
              color: REFINED.ink,
              fontFamily: "inherit",
              fontSize: 13,
            }}
          />
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: REFINED.ink2, letterSpacing: "0.4px" }}>BIS</span>
          <input
            type="datetime-local"
            value={to ? toLocalInput(to) : ""}
            onChange={(e) => onInput("to", e.target.value)}
            style={{
              height: 34,
              border: `1.5px solid ${REFINED.line}`,
              borderRadius: 8,
              padding: "0 8px",
              color: REFINED.ink,
              fontFamily: "inherit",
              fontSize: 13,
            }}
          />
        </div>
        <span style={{ marginLeft: "auto", fontSize: 12, color: REFINED.ink2, fontWeight: 600 }}>
          {filtered.length} {filtered.length === 1 ? "Eintrag" : "Einträge"}
        </span>

        {/* ── Arten (leer = alle) ── */}
        {vorhandeneArten.length > 1 && (
          <div style={{ flexBasis: "100%", display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
            <button
              type="button"
              onClick={() => setArten(new Set())}
              style={{
                border: `1.5px solid ${arten.size === 0 ? REFINED.aub : REFINED.line}`,
                backgroundColor: arten.size === 0 ? REFINED.aub : "#fff",
                color: arten.size === 0 ? "#fff" : REFINED.ink2,
                borderRadius: 999,
                padding: "4px 12px",
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              Alle Arten
            </button>
            {vorhandeneArten.map((k) => {
              const active = arten.has(k);
              const c = KIND_COLORS[k];
              return (
                <button
                  key={k}
                  type="button"
                  onClick={() => toggleArt(k)}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 5,
                    border: `1.5px solid ${active ? c.fg : REFINED.line}`,
                    backgroundColor: active ? c.bg : "#fff",
                    color: active ? c.fg : REFINED.ink2,
                    borderRadius: 999,
                    padding: "4px 12px",
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: "pointer",
                    fontFamily: "inherit",
                  }}
                >
                  {KIND_ICONS[k]}
                  {KIND_LABELS[k]}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Liste ── */}
      {loading ? (
        <div style={{ color: REFINED.ink2, fontSize: 13 }}>Archiv wird geladen…</div>
      ) : filtered.length === 0 ? (
        <div style={{ color: REFINED.ink2, fontSize: 13 }}>
          {entries.length === 0
            ? "Für diesen Kontakt sind keine Verlaufseinträge vorhanden."
            : arten.size > 0
              ? "Keine Einträge dieser Art im gewählten Zeitraum."
              : "Keine Einträge im gewählten Zeitraum."}
        </div>
      ) : (
        filtered.map((e) => (
          <ArchivRow
            key={e.id}
            entry={e}
            open={open.has(e.id)}
            onToggle={() => toggle(e.id)}
            {...(e.ziel ? { onOeffnen: () => navigate(e.ziel as string) } : {})}
          />
        ))
      )}
    </div>
  );
};

// ── Ausklappbare Zeile (Muster VorgangTodoRow) ───────────────────────────────
const ArchivRow = ({
  entry,
  open,
  onToggle,
  onOeffnen,
}: {
  entry: ArchiveEntry;
  open: boolean;
  onToggle: () => void;
  /** Gesetzt, wenn es etwas zu öffnen gibt — dann wird der Titel zum Sprung. */
  onOeffnen?: () => void;
}) => {
  const c = KIND_COLORS[entry.kind];
  return (
    <div style={{ border: `1px solid ${REFINED.line}`, borderRadius: 8, overflow: "hidden" }}>
      <div
        onClick={onToggle}
        role="button"
        tabIndex={0}
        style={{
          display: "grid",
          gridTemplateColumns: "20px 150px 150px minmax(0, 1fr)",
          gap: 12,
          alignItems: "center",
          padding: "10px 12px",
          backgroundColor: open ? REFINED.aub50 : "#fff",
          cursor: "pointer",
        }}
      >
        <span style={{ color: REFINED.ink2, fontSize: 13, textAlign: "center" }}>{open ? "▾" : "▸"}</span>
        <span style={{ fontSize: 12.5, fontWeight: 600, color: REFINED.ink2, whiteSpace: "nowrap" }}>
          {fmtDateTime(entry.ts)}
        </span>
        <span>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "2px 10px",
              borderRadius: 999,
              fontSize: 11,
              fontWeight: 700,
              backgroundColor: c.bg,
              color: c.fg,
              whiteSpace: "nowrap",
            }}
          >
            {KIND_ICONS[entry.kind]}
            {KIND_LABELS[entry.kind]}
          </span>
        </span>
        <span style={{ minWidth: 0 }}>
          {entry.ziel && onOeffnen ? (
            // Ein echter Link (mit `href`), damit er sich in einem neuen Tab
            // öffnen lässt; der Klick selbst geht über den Router. Er hält den
            // Klick bei sich (`stopPropagation`) — sonst klappte die Zeile im
            // selben Moment auf, in dem sie verlassen wird.
            <a
              href={entry.ziel}
              title="Im Vorgang öffnen"
              onClick={(ev) => {
                ev.preventDefault();
                ev.stopPropagation();
                onOeffnen();
              }}
              style={{
                display: "block",
                fontSize: 14,
                fontWeight: 600,
                color: REFINED.aub,
                textDecorationLine: "none",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {entry.title}
            </a>
          ) : (
            <span
              style={{
                display: "block",
                fontSize: 14,
                fontWeight: 600,
                color: REFINED.ink,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {entry.title}
            </span>
          )}
          {!open && entry.summary && (
            <span
              style={{
                display: "block",
                fontSize: 12,
                color: REFINED.ink2,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {entry.summary}
            </span>
          )}
        </span>
      </div>

      {open && (
        <div
          style={{
            padding: "12px 16px 14px 44px",
            backgroundColor: "#fafafa",
            borderTop: `1px solid ${REFINED.line}`,
            display: "flex",
            flexDirection: "column",
            gap: 12,
          }}
        >
          {(entry.details ?? []).map((d, i) => (
            <div key={i}>
              {d.label && (
                <div
                  style={{
                    fontSize: 11,
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    color: REFINED.aub,
                    fontWeight: 700,
                    marginBottom: 3,
                  }}
                >
                  {d.label}
                </div>
              )}
              {d.text ? (
                <div style={{ fontSize: 13, color: REFINED.ink, whiteSpace: "pre-wrap" }}>{d.text}</div>
              ) : (
                <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, flexWrap: "wrap" }}>
                  <span style={{ color: "#8b1c1c", textDecorationLine: "line-through", overflowWrap: "anywhere" }}>
                    {d.old == null || d.old === "" ? "—" : d.old}
                  </span>
                  <span style={{ color: REFINED.ink2 }}>→</span>
                  <span style={{ color: "#1b5e20", fontWeight: 600, overflowWrap: "anywhere" }}>
                    {d.neu == null || d.neu === "" ? "—" : d.neu}
                  </span>
                </div>
              )}
            </div>
          ))}
          {entry.actor && <div style={{ fontSize: 12, color: REFINED.ink2 }}>{entry.actor}</div>}
        </div>
      )}
    </div>
  );
};

export default ContactArchiv;
